import React, { useState } from 'react';
import { Search, SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import Header from './components/Header5';
import Footer from './components/Footer5';
import ProductCard from './components/ProductCard';
import Button from './components/Button';

/**
 * Template 5: TARA — Shop Page
 * Editorial grid layout with refined filtering
 */
const Shop = () => {
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [sortBy, setSortBy] = useState('featured');
    const [showFilters, setShowFilters] = useState(false);

    const categories = ['All', 'Sarees', 'Lehengas', 'Kurtas', 'Indo-Western'];
    const sortOptions = [
        { value: 'featured', label: 'Featured' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Newest First' },
    ];

    const products = [
        { id: 1, name: 'Chanderi Silk Saree', category: 'Sarees', price: 12999, image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Embroidered Lehenga', category: 'Lehengas', price: 34999, image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Banarasi Silk Kurta', category: 'Kurtas', price: 8499, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Designer Anarkali', category: 'Indo-Western', price: 18999, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Kanjivaram Drape', category: 'Sarees', price: 24999, image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Zardozi Lehenga', category: 'Lehengas', price: 29999, image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Organza Cape Set', category: 'Indo-Western', price: 14999, image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Heritage Silk Saree', category: 'Sarees', price: 4999, image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 9, name: 'Bridal Lehenga Set', category: 'Lehengas', price: 34999, originalPrice: 42999, image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 10, name: 'Printed Silk Kurta', category: 'Kurtas', price: 6999, image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 11, name: 'Tussar Silk Saree', category: 'Sarees', price: 15999, image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 12, name: 'Fusion Jacket Set', category: 'Indo-Western', price: 11999, image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    const filteredProducts = selectedCategory === 'All'
        ? products
        : products.filter((p) => p.category === selectedCategory);

    return (
        <div className="w-full min-h-screen" style={{ backgroundColor: '#FAF9F6', fontFamily: "'Outfit', sans-serif" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Outfit:wght@300;400;500;600&display=swap');
            `}</style>
            <Header />

            <main className="w-full">
                {/* Hero Banner */}
                <section className="pt-8 md:pt-12 pb-8 md:pb-10 px-4 md:px-6 lg:px-8 text-center border-b border-[#E4E4E7]" style={{ backgroundColor: '#FAF9F6' }}>
                    <div className="max-w-[800px] mx-auto">
                        <p
                            className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#71717A] mb-3"
                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                        >
                            Curated for You
                        </p>
                        <h1
                            className="text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] mb-3"
                            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                        >
                            The Collection
                        </h1>
                        <p
                            className="text-sm md:text-base text-[#71717A] font-light leading-relaxed"
                        >
                            Discover handcrafted pieces that celebrate Indian artistry with editorial elegance
                        </p>
                    </div>
                </section>

                {/* Filter & Sort Bar */}
                <section className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
                    {/* Desktop Filters */}
                    <div className="hidden md:flex items-center justify-between">
                        <div className="flex items-center gap-6">
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`relative text-xs tracking-[0.12em] uppercase pb-1 transition-all duration-300 ${selectedCategory === cat ? 'text-[#1A1A1A]' : 'text-[#71717A] hover:text-[#1A1A1A]'}`}
                                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: selectedCategory === cat ? 500 : 400 }}
                                >
                                    {cat}
                                    {selectedCategory === cat && (
                                        <span className="absolute bottom-0 left-0 w-full h-px bg-[#2C2C2C]" />
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-[#71717A] tracking-wide" style={{ fontWeight: 300 }}>Sort by:</span>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="text-xs text-[#1A1A1A] bg-transparent outline-none cursor-pointer tracking-wide"
                                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                            >
                                {sortOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Mobile Filters */}
                    <div className="md:hidden flex items-center justify-between">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center gap-2 text-xs tracking-[0.1em] uppercase text-[#1A1A1A]"
                            style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                        >
                            <SlidersHorizontal className="w-4 h-4" /> Filter
                        </button>
                        <p className="text-xs text-[#71717A]" style={{ fontWeight: 300 }}>
                            {filteredProducts.length} pieces
                        </p>
                    </div>

                    {/* Mobile Filter Drawer */}
                    {showFilters && (
                        <div className="md:hidden mt-4 pt-4 border-t border-[#E4E4E7]">
                            <div className="flex flex-wrap gap-2 mb-4">
                                {categories.map((cat) => (
                                    <button
                                        key={cat}
                                        onClick={() => { setSelectedCategory(cat); setShowFilters(false); }}
                                        className={`px-4 py-2 text-xs tracking-[0.1em] uppercase transition-all duration-300 ${selectedCategory === cat ? 'bg-[#2C2C2C] text-white' : 'border border-[#E4E4E7] text-[#71717A] hover:border-[#2C2C2C] hover:text-[#1A1A1A]'}`}
                                        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="w-full px-4 py-2.5 text-xs text-[#1A1A1A] border border-[#E4E4E7] bg-transparent outline-none"
                                style={{ fontFamily: "'Outfit', sans-serif" }}
                            >
                                {sortOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </section>

                {/* Product Grid */}
                <section className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pb-16 md:pb-24">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {filteredProducts.map((product) => (
                            <ProductCard key={product.id} product={product} />
                        ))}
                    </div>

                    {filteredProducts.length === 0 && (
                        <div className="text-center py-20">
                            <p
                                className="text-lg text-[#71717A] italic"
                                style={{ fontFamily: "'Cormorant Garamond', serif" }}
                            >
                                No pieces found in this collection.
                            </p>
                        </div>
                    )}
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default Shop;
